/**
 * Timezone Conversion Utilities (Client-side)
 */

/**
 * Convert time from one timezone to another
 */
export function convertTime(
  time: string,
  fromTimezone: string,
  toTimezone: string,
  date?: string
): string {
  try {
    if (!time || !fromTimezone || !toTimezone) {
      return time;
    }

    const targetDate = date || new Date().toISOString().split('T')[0];
    const dateTimeString = `${targetDate}T${time}:00`;

    // Create a date object assuming the time is in fromTimezone
    // We'll use a workaround since JavaScript doesn't directly support timezone conversion
    const tempDate = new Date(dateTimeString);
    
    // Get the time in the target timezone
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: toTimezone,
      hour12: false,
      hour: '2-digit',
      minute: '2-digit'
    });

    const parts = formatter.formatToParts(tempDate);
    const hour = parts.find(p => p.type === 'hour')?.value || '00';
    const minute = parts.find(p => p.type === 'minute')?.value || '00';

    return `${hour}:${minute}`;
  } catch (error) {
    console.error('Timezone conversion error:', error);
    return time;
  }
}

/**
 * Convert time slot to user's timezone
 */
export function convertTimeSlot(
  timeSlot: { startTime: string; endTime: string; [key: string]: any },
  fromTimezone: string,
  toTimezone: string,
  date?: string
) {
  return {
    ...timeSlot,
    startTime: convertTime(timeSlot.startTime, fromTimezone, toTimezone, date),
    endTime: convertTime(timeSlot.endTime, fromTimezone, toTimezone, date),
    originalTimezone: fromTimezone,
    convertedTimezone: toTimezone
  };
}

/**
 * Convert multiple time slots
 */
export function convertTimeSlots(
  timeSlots: Array<{ startTime: string; endTime: string; [key: string]: any }>,
  fromTimezone: string,
  toTimezone: string,
  date?: string
) {
  if (!Array.isArray(timeSlots)) {
    return [];
  }

  return timeSlots.map(slot => convertTimeSlot(slot, fromTimezone, toTimezone, date));
}

/**
 * Format time for display
 */
export function formatTime(time: string): string {
  if (!time) return '';
  
  const [hours, minutes] = time.split(':');
  const hour = parseInt(hours);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;
  
  return `${displayHour}:${minutes} ${ampm}`;
}

